/**
 * HeroSlideshow Component - Brutalist Auto-Advancing Slideshow
 *
 * Full-width hero slideshow with:
 * - 3 slides with auto-advance (10 seconds)
 * - Manual navigation (prev/next arrows)
 * - Slide indicators/dots
 * - Pause on hover
 * - Brutalist design system styling
 * - Smooth transitions
 * - Responsive design
 * - Keyboard navigation support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../Button';
import { LazyImage } from '../LazyImage';

interface Slide {
  id: number;
  image: string;
  imageAlt: string;
  title: string;
  subtitle: string;
  primaryCTA: { text: string; link: string };
  secondaryCTA: { text: string; link: string };
}

interface HeroSlideshowProps {
  autoAdvanceInterval?: number; // default 10000ms
}

export const HeroSlideshow: React.FC<HeroSlideshowProps> = ({
  autoAdvanceInterval = 10000,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slide content data
  const slides: Slide[] = [
    {
      id: 1,
      image: '/images/hero/hero-healthcare-bls-training.png',
      imageAlt: 'Healthcare professionals practicing BLS CPR on mannequins in training room',
      title: 'BLS HEALTHCARE CERTIFICATION',
      subtitle: 'American Heart Association Certified • Same-Day Cards • Hands-On Training',
      primaryCTA: { text: 'Book BLS Training', link: '/courses/bls-healthcare' },
      secondaryCTA: { text: 'View All Courses', link: '/courses' },
    },
    {
      id: 2,
      image: '/images/hero/hero-certification-success.png',
      imageAlt: 'Group of successful CPR certification students giving thumbs up',
      title: 'GET CERTIFIED. SAVE LIVES.',
      subtitle: 'American Heart Association Certified • Orlando & Atlanta • Professional Instructors',
      primaryCTA: { text: 'Start Your Certification', link: '/book' },
      secondaryCTA: { text: 'Read Testimonials', link: '/testimonials' },
    },
    {
      id: 3,
      image: '/images/hero/hero-group-onsite-training.png',
      imageAlt: 'Large group on-site CPR training session in classroom setting',
      title: 'ON-SITE GROUP TRAINING',
      subtitle: 'Mobile Training • 10+ Attendees • Flexible Scheduling • We Come to You',
      primaryCTA: { text: 'Request Group Training', link: '/courses/group-training' },
      secondaryCTA: { text: 'Get a Quote', link: '/book' },
    },
  ];

  // Auto-advance functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoAdvanceInterval);

    return () => clearInterval(timer);
  }, [autoAdvanceInterval, slides.length]);

  // Navigation handlers
  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevSlide, goToNextSlide]);

  // CTA handlers
  const handlePrimaryCTAClick = (link: string) => {
    window.location.href = link;
  };

  const handleSecondaryCTAClick = (link: string) => {
    window.location.href = link;
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section
      className="relative min-h-[80vh] flex items-center justify-center overflow-hidden"
      aria-label="Hero slideshow"
      aria-live="polite"
    >
      {/* BACKGROUND IMAGES: All slides rendered, controlled by opacity */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 z-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <LazyImage
            src={slide.image}
            alt={slide.imageAlt}
            className="w-full h-full object-cover"
            priority={index === 0}
          />
          {/* CONTRAST OVERLAY: Gradient from brand colors - light overlay to showcase images */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo_dye-900/35 to-amaranth_purple-900/35" />
        </div>
      ))}

      {/* NAVIGATION ARROWS: Brutalist geometric styling */}
      <button
        onClick={goToPrevSlide}
        className="absolute left-4 md:left-8 z-20 w-14 h-14 md:w-16 md:h-16 bg-white border-4 border-rich_black-500 shadow-brutalist hover:shadow-brutalist-hover hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 flex items-center justify-center group"
        aria-label="Previous slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={3}
          stroke="currentColor"
          className="w-6 h-6 md:w-8 md:h-8 text-rich_black-500 group-hover:text-amaranth_purple-500"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>

      <button
        onClick={goToNextSlide}
        className="absolute right-4 md:right-8 z-20 w-14 h-14 md:w-16 md:h-16 bg-white border-4 border-rich_black-500 shadow-brutalist hover:shadow-brutalist-hover hover:translate-x-1 hover:-translate-y-1 transition-all duration-200 flex items-center justify-center group"
        aria-label="Next slide"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={3}
          stroke="currentColor"
          className="w-6 h-6 md:w-8 md:h-8 text-rich_black-500 group-hover:text-amaranth_purple-500"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>

      {/* CONTENT: Bold typography with maximum impact */}
      <div className="relative z-10 text-center px-6 max-w-7xl stagger-children">
        {/* MASSIVE HEADLINE: Oswald display font, uppercase, ultra-wide tracking, enhanced contrast */}
        <h1
          className="font-display font-bold uppercase tracking-[0.15em] text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl text-white mb-6 md:mb-8 leading-[0.9] transition-opacity duration-700"
          style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.9), 0 4px 8px rgba(0,0,0,0.7), 0 8px 16px rgba(0,0,0,0.5), 0 0 40px rgba(0,0,0,0.6)',
          }}
          key={`title-${currentSlide}`}
        >
          {currentSlideData.title}
        </h1>

        {/* SUBTITLE: Larger, bolder, uppercase for brutalist impact, enhanced contrast */}
        <p
          className="font-sans text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-white mb-8 md:mb-12 uppercase tracking-wide transition-opacity duration-700"
          style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.9), 0 4px 8px rgba(0,0,0,0.7), 0 8px 16px rgba(0,0,0,0.5)',
          }}
          key={`subtitle-${currentSlide}`}
        >
          {currentSlideData.subtitle}
        </p>

        {/* CTAs: Brutalist buttons with lift effects */}
        <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center">
          <Button
            variant="primary"
            size="xl"
            onClick={() => handlePrimaryCTAClick(currentSlideData.primaryCTA.link)}
            aria-label={currentSlideData.primaryCTA.text}
          >
            {currentSlideData.primaryCTA.text}
          </Button>

          <Button
            variant="outline"
            size="xl"
            onClick={() => handleSecondaryCTAClick(currentSlideData.secondaryCTA.link)}
            className="bg-white border-white text-rich_black-500 hover:bg-indigo_dye-600 hover:text-white hover:border-indigo_dye-600"
            aria-label={currentSlideData.secondaryCTA.text}
          >
            {currentSlideData.secondaryCTA.text}
          </Button>
        </div>
      </div>

      {/* SLIDE INDICATORS: Brutalist dots at bottom */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => goToSlide(index)}
            className={`w-4 h-4 border-4 border-white transition-all duration-300 ${
              index === currentSlide
                ? 'bg-amaranth_purple-500 scale-125 shadow-brutalist-sm'
                : 'bg-white/50 hover:bg-white hover:scale-110'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentSlide ? 'true' : 'false'}
          />
        ))}
      </div>

      {/* GEOMETRIC ACCENT: Bottom gradient for visual grounding */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-rich_black-500 to-transparent" />
    </section>
  );
};
